import React, { useState } from 'react';
import axios from 'axios';
import { Upload, FileSpreadsheet, ArrowRight, CheckCircle2, AlertTriangle, Eye, Download } from 'lucide-react';
import { theme, cardStyle, btnStyle, inputStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  DATA MIGRATION WIZARD — Excel/CSV bulk import
//  Steps: 1) Upload+entity → 2) Map columns → 3) Dry-run → 4) Import
// ═══════════════════════════════════════════════════════════════════

const ENTITIES = [
  { key: 'patient',     label: '👥 Patients', desc: 'Patient master from old PMS' },
  { key: 'appointment', label: '📅 Appointments', desc: 'Past or future appointment list' },
];

const DataMigrationPage = ({ API_BASE, currentUser }) => {
  const [step, setStep] = useState(1);
  const [entity, setEntity] = useState('patient');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dryResult, setDryResult] = useState(null);
  const [finalResult, setFinalResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const upload = async () => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('entity', entity);
      const r = await axios.post(`${API_BASE}/migration/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
      setMapping(r.data.suggestedMapping || {});
      setStep(2);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  const runImport = async (dryRun) => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API_BASE}/migration/import`, {
        filePath: preview.filePath, mapping, entity, dryRun,
      });
      if (dryRun) setDryResult(r.data);
      else { setFinalResult(r.data); setStep(4); }
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  const reset = () => {
    setStep(1); setFile(null); setPreview(null); setMapping({}); setDryResult(null); setFinalResult(null); setErr('');
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><Upload size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> Data Migration Wizard</h2>
        <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
          Excel / CSV se patient + appointment data import karo (Dentrix, Dental4Windows, etc.)
        </p>
      </div>

      {/* Stepper */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {['Upload', 'Map Columns', 'Dry Run', 'Import'].map((s, i) => (
          <div key={s} style={{
            flex: 1, padding: 10, borderRadius: 8,
            background: step > i ? '#16a34a' : step === i + 1 ? theme.primary : theme.borderLight,
            color: step >= i + 1 ? 'white' : theme.textMuted,
            fontWeight: 700, fontSize: 13, textAlign: 'center',
          }}>
            {step > i + 1 ? '✓ ' : `${i + 1}. `}{s}
          </div>
        ))}
      </div>

      {err && <div style={{ ...cardStyle, padding: 10, background: '#fee2e2', color: '#991b1b', marginBottom: 12, fontWeight: 600 }}>⚠ {err}</div>}

      {/* STEP 1 */}
      {step === 1 && (
        <div style={{ ...cardStyle, padding: 18 }}>
          <h3 style={{ margin: '0 0 10px', color: theme.navy }}>1. Choose entity & upload file</h3>
          <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
            {ENTITIES.map(e => (
              <label key={e.key} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: 12,
                border: `2px solid ${entity === e.key ? theme.primary : theme.border}`,
                borderRadius: 10, cursor: 'pointer', background: entity === e.key ? theme.primary + '12' : 'white',
              }}>
                <input type="radio" checked={entity === e.key} onChange={() => setEntity(e.key)} />
                <div>
                  <div style={{ fontWeight: 700, color: theme.navy }}>{e.label}</div>
                  <div style={{ fontSize: 12, color: theme.textMuted }}>{e.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files[0])} style={{ ...inputStyle, padding: 8 }} />
          {file && <div style={{ marginTop: 8, fontSize: 13, color: theme.textMuted }}><FileSpreadsheet size={14} style={{ verticalAlign: -2, marginRight: 4 }} />{file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
          <button onClick={upload} disabled={!file || busy} style={{ ...btnStyle, marginTop: 14 }}>
            {busy ? 'Parsing…' : <>Upload & Preview <ArrowRight size={14} style={{ marginLeft: 6, verticalAlign: -2 }} /></>}
          </button>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && preview && (
        <div style={{ ...cardStyle, padding: 18 }}>
          <h3 style={{ margin: '0 0 6px', color: theme.navy }}>2. Map your columns to {entity} fields</h3>
          <p style={{ fontSize: 12, color: theme.textMuted, margin: '0 0 14px' }}>
            File: <strong>{preview.totalRows} rows</strong> detected. Required: {preview.requiredFields.join(', ')}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
            {preview.template.map(field => {
              const required = preview.requiredFields.includes(field);
              return (
                <div key={field}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', marginBottom: 2 }}>
                    {field}{required && <span style={{ color: theme.danger }}> *</span>}
                  </label>
                  <select value={mapping[field] || ''} onChange={e => setMapping({ ...mapping, [field]: e.target.value })} style={inputStyle}>
                    <option value="">— skip —</option>
                    {preview.headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              );
            })}
          </div>

          <details style={{ marginBottom: 12 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, color: theme.primary }}><Eye size={14} style={{ verticalAlign: -2, marginRight: 4 }} />Preview first 50 rows</summary>
            <div style={{ overflowX: 'auto', maxHeight: 300, marginTop: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead style={{ position: 'sticky', top: 0, background: theme.bg }}>
                  <tr>{preview.headers.map(h => <th key={h} style={{ padding: 5, border: `1px solid ${theme.border}`, textAlign: 'left' }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i}>{preview.headers.map(h => <td key={h} style={{ padding: 5, border: `1px solid ${theme.border}` }}>{String(r[h] || '')}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={reset} style={{ ...btnStyle, marginTop: 0, background: 'white', color: theme.navy, border: `1px solid ${theme.border}` }}>← Restart</button>
            <button onClick={() => { runImport(true); setStep(3); }} disabled={busy} style={{ ...btnStyle, marginTop: 0 }}>
              Dry Run <ArrowRight size={14} style={{ marginLeft: 6, verticalAlign: -2 }} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Dry run result */}
      {step === 3 && (
        <div style={{ ...cardStyle, padding: 18 }}>
          <h3 style={{ margin: '0 0 10px' }}>3. Dry Run Results</h3>
          {busy ? <div>Running…</div> : dryResult ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
                <Stat icon={<FileSpreadsheet color="#2563eb" />} label="Total" value={dryResult.total} />
                <Stat icon={<CheckCircle2 color="#16a34a" />} label="Will Create" value={dryResult.created} />
                <Stat icon={<AlertTriangle color="#f59e0b" />} label="Will Skip" value={dryResult.skipped} />
                <Stat icon={<AlertTriangle color="#dc2626" />} label="Failed" value={dryResult.failed} />
              </div>
              {dryResult.errors.length > 0 && (
                <div style={{ padding: 10, background: '#fef3c7', borderRadius: 8, marginBottom: 12, maxHeight: 200, overflowY: 'auto' }}>
                  <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>⚠ First {dryResult.errors.length} issues:</div>
                  {dryResult.errors.map((e, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#78350f' }}>Row {e.row}: {e.msg || `missing ${e.missing?.join(', ')}`}</div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setStep(2)} style={{ ...btnStyle, marginTop: 0, background: 'white', color: theme.navy, border: `1px solid ${theme.border}` }}>← Back to Map</button>
                <button onClick={() => runImport(false)} disabled={busy || dryResult.created === 0} style={{ ...btnStyle, marginTop: 0, background: theme.success }}>
                  ✓ Confirm Import {dryResult.created} records
                </button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* STEP 4: Final */}
      {step === 4 && finalResult && (
        <div style={{ ...cardStyle, padding: 24, textAlign: 'center' }}>
          <CheckCircle2 size={56} color="#16a34a" style={{ marginBottom: 10 }} />
          <h2 style={{ color: '#065f46', margin: '0 0 8px' }}>Import Complete!</h2>
          <p style={{ fontSize: 16, color: theme.navy, margin: '0 0 16px' }}>
            Successfully imported <strong>{finalResult.created}</strong> records · {finalResult.skipped} skipped · {finalResult.failed} failed
          </p>
          <button onClick={reset} style={{ ...btnStyle, marginTop: 0 }}>Start New Import</button>
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon, label, value }) => (
  <div style={{ ...cardStyle, padding: 12, textAlign: 'center' }}>
    <div style={{ marginBottom: 4 }}>{icon}</div>
    <div style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: theme.navy }}>{value}</div>
  </div>
);

export default DataMigrationPage;
